import React from "react";
import PropTypes from "prop-types";

import HelpTrigger from "@/components/help-trigger/HelpTrigger";
import DynamicComponent from "@/components/DynamicComponent";
import { Indexer as IndexerType } from "@/components/proptypes";

import Form from "antd/lib/form";
import Button from "antd/lib/button";

import Title from "./Title";
import IndexerDestinations from "./IndexerDestinations";
import Query from "./Query";
import IndexerInsertStrategy from "./IndexerInsertStrategy";
import IndexerTargetTable from "./IndexerTargetTable";

import HorizontalFormItem from "../HorizontalFormItem";

export default class IndexerEdit extends React.Component {
  _isMounted = false;

  state = {
    saving: false,
  };

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  save = () => {
    this.setState({ saving: true });
    this.props.save().catch((error) => {
      if (this._isMounted) {
        this.setState({ saving: false });
      }
    });
  };

  cancel = () => {
    this.props.cancel();
  };

  handleDataSourceSelect = (dataSource) => {
    // Update the indexer with the selected data source
    if (dataSource) {
      this.props.onCriteriaChange({ 
        ...this.props.indexer.options,
        data_source_id: dataSource.id 
      });
    } else {
      // Handle deselection if needed
      const options = { ...this.props.indexer.options };
      delete options.data_source_id;
      this.props.onCriteriaChange(options);
    }
  };

  handleTargetTableChange = (e) => {
    // Use a more appropriate prop for updating non-criteria options
    this.props.onOptionsChange({
      target_table: e.target.value
    });
  };

  handleDeleteDuplicatesChange = (e) => {
    // Use a more appropriate prop for updating non-criteria options
    this.props.onOptionsChange({
      delete_duplicates: e.target.checked
    });
  };

  handleInsertStrategyChange = (e) => {
    // Update the insert_strategy option
    this.props.onOptionsChange({
      insert_strategy: e.target.value
    });
  };

  render() {
    const { indexer, queryResult, onQuerySelected, onNameChange, onCriteriaChange, 
            onOptionsChange, menuButton, cancel } = this.props;
    const { query, name, options } = indexer;
    const { saving } = this.state;

    return (
      <>
        <Title indexer={indexer} name={name} onChange={onNameChange} editMode canEdit menuButton={menuButton} />
        <div className="bg-white tiled p-20">
          <div className="d-flex">
            <Form className="flex-fill">
              <HorizontalFormItem label="Query">
                <Query query={query} queryResult={queryResult} onChange={onQuerySelected} editMode />
              </HorizontalFormItem>
              {queryResult && options && (
                <>
                  <HorizontalFormItem label="Destination">
                    <IndexerDestinations 
                      indexerId={indexer.id} 
                      onDataSourceSelect={this.handleDataSourceSelect}
                      dataSourceId={options && options.data_source_id ? options.data_source_id : null}
                    />
                  </HorizontalFormItem>
                  <HorizontalFormItem label="Target Table">
                    <IndexerTargetTable 
                      targetTable={options.target_table}
                      onChange={value => this.props.onOptionsChange({ target_table: value })}
                    />
                  </HorizontalFormItem>
                  <HorizontalFormItem label="Insert Strategy">
                    <IndexerInsertStrategy 
                      insertStrategy={options.insert_strategy}
                      onChange={value => this.props.onOptionsChange({ insert_strategy: value })}
                    />
                  </HorizontalFormItem>
                  <HorizontalFormItem label="Options">
                    <div className="form-check">
                      <input
                        id="deleteDuplicatesCheckbox"
                        type="checkbox"
                        className="form-check-input"
                        checked={!!options.delete_duplicates}
                        onChange={this.handleDeleteDuplicatesChange}
                      />
                      <label className="form-check-label" htmlFor="deleteDuplicatesCheckbox">
                        Remove duplicates when indexing
                      </label>
                    </div>
                  </HorizontalFormItem>
                </>
              )}
              <HorizontalFormItem>
                <Button type="primary" onClick={this.save} disabled={!query} className="m-r-10">
                  {saving && (
                    <span role="status" aria-live="polite" aria-relevant="additions removals">
                      <i className="fa fa-spinner fa-pulse m-r-5" aria-hidden="true" />
                      <span className="sr-only">Saving...</span>
                    </span>
                  )}
                  Save
                </Button>
                <Button onClick={this.cancel}>Cancel</Button>
              </HorizontalFormItem>
            </Form>
            <div>
              <HelpTrigger className="f-13" type="INDEXER_SETUP">
                Setup Instructions <i className="fa fa-question-circle" aria-hidden="true" />
                <span className="sr-only">(help)</span>
              </HelpTrigger>
            </div>
          </div>
        </div>
      </>
    );
  }
}

IndexerEdit.propTypes = {
  indexer: IndexerType.isRequired,
  queryResult: PropTypes.object,
  menuButton: PropTypes.node.isRequired,
  save: PropTypes.func.isRequired,
  cancel: PropTypes.func.isRequired,
  onQuerySelected: PropTypes.func.isRequired,
  onNameChange: PropTypes.func.isRequired,
  onCriteriaChange: PropTypes.func.isRequired,
  onOptionsChange: PropTypes.func.isRequired,
};

IndexerEdit.defaultProps = {
  queryResult: null,
};
